import { Combobox } from "@headlessui/react";
import { useState, Fragment } from "react";
import Dialog from "@mui/material/Dialog";
import { MagnifyingGlassIcon } from "./icons/MagnifyingGlassIcon";
import { MAX_GUESSES } from "../lib/utils/constants";
import { gridLevelNumber } from "../lib/utils/gridLevels";
import { trackLevelStart } from "../lib/utils/analytics";
import { IconCheckLg } from "./icons/IconCheckLg";
import { UndoButton } from "./buttons/UndoButton";
import PickDialog from "./modals/PickDialog";
import { rarityScheme } from "../lib/utils/rarityScheme";
import useGameStore from "../lib/store/GameStore";
import usePersistentStore from "../lib/store/PersistentStore";
import { PokemonImage } from "./PokemonImage";

function getKeyOfMaxValue(obj) {
  let maxKey = null;
  let maxValue = Number.NEGATIVE_INFINITY;

  for (const key in obj) {
    if (obj.hasOwnProperty(key) && obj[key] > maxValue) {
      maxKey = key;
      maxValue = obj[key];
    }
  }

  return maxKey;
}

export default function CellButton({ row, col, selectedTab }) {
  const [
    grid,
    setIsResultOpen,
    analyticsGameName,
    globalStats,
    undoModeActive,
    user,
    setIsRegisterOpen,
    pokemonList,
    pokemonById,
  ] = useGameStore((state) => [
    state.grid,
    state.setIsResultOpen,
    state.analyticsGameName,
    state.globalStats,
    state.undoModeActive,
    state.user,
    state.setIsRegisterOpen,
    state.pokemonList,
    state.pokemonById,
  ]);

  const [
    persistGuess,
    undoGuess,
    guessesLeft,
    incorrectGuesses,
    correctGuesses,
    undoUsed,
    seenRarity,
    setSeenRarity,
    cardMode,
  ] = usePersistentStore((state) => [
    state.persistGuess,
    state.undoGuess,
    state.guessesLeft,
    state.incorrectGuesses,
    state.correctGuesses,
    state.undoUsed,
    state.seenRarity,
    state.setSeenRarity,
    state.cardMode,
  ]);
  const storedCorrectGuessId = correctGuesses[row - 1][col - 1];
  const storedCorrectPokemon = pokemonById.get(storedCorrectGuessId);
  const otherCorrectGuesses = correctGuesses.flat();
  const correctAnswers = Object.keys(
    grid.correctAnswersPlayerMap[row - 1][col - 1]
  ).map((pokemonId) => parseInt(pokemonId));
  const cellGuesses = incorrectGuesses[row - 1][col - 1];

  const [isOpen, setIsOpen] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(false);
  const [query, setQuery] = useState("");

  const correctAnswersPlayerMap =
    globalStats?.correctAnswersPlayerMap[row - 1][col - 1];
  const mostPopularId = getKeyOfMaxValue(correctAnswersPlayerMap);
  const mostPopularPokemon = pokemonById.get(Number(mostPopularId));

  const displayPokemon =
    selectedTab == "popular" ? mostPopularPokemon : storedCorrectPokemon;

  const selectedPct =
    displayPokemon && correctAnswersPlayerMap
      ? (correctAnswersPlayerMap[displayPokemon.id] / globalStats?.completed) *
        100
      : 0.1;

  const filteredPeople =
    query.length < 1
      ? []
      : pokemonList
          .filter((pokemon) => {
            return (
              !pokemon.hidden &&
              pokemon.display_name
                .split(/[ \-.'":]/)
                .join("")
                .toLowerCase()
                .includes(
                  query
                    .split(/[ \-.'":]/)
                    .join("")
                    .toLowerCase()
                )
            );
          })
          .slice(0, 20);

  function closeModal() {
    setIsOpen(false);
    setSelectedPerson(false);
    setQuery("");
  }

  function openModal() {
    if (grid.levelNumber !== gridLevelNumber && !user) {
      // Premum feature check
      setIsRegisterOpen(true);
    } else if (
      guessesLeft > 0 &&
      !storedCorrectPokemon &&
      selectedTab != "popular"
    ) {
      setIsOpen(true);
    }
  }

  function undoHandler(pokemon) {
    if (selectedTab == "my") {
      undoGuess(pokemon, row, col);
    }
  }

  function selectPokemon(pokemon) {
    if (guessesLeft === MAX_GUESSES) {
      trackLevelStart({
        gameName: analyticsGameName,
        levelNumber: gridLevelNumber,
      });
    }
    setSelectedPerson(pokemon);
    persistGuess(pokemon, row, col);

    if (correctAnswers.includes(pokemon.id)) {
      const pct = correctAnswersPlayerMap
        ? (correctAnswersPlayerMap[pokemon.id] / globalStats?.completed) * 100
        : 0.1;
      const scheme = rarityScheme(pct);

      if (cardMode && (scheme.level > seenRarity || scheme.level == 9)) {
        closeModal();
        openPickModal();
        setSeenRarity(scheme.level);
      } else {
        setTimeout(() => {
          closeModal();
        }, 700);
      }
    }

    if (guessesLeft <= 1) {
      closeModal();
      setIsResultOpen(true);
    }
  }

  const [isPickOpen, setIsPickOpen] = useState(false);
  function closePickModal() {
    setIsPickOpen(false);
  }

  function openPickModal() {
    if (cardMode && selectedTab == "my") {
      setIsPickOpen(true);
    }
  }

  const scheme = rarityScheme(selectedPct);
  const bg = cardMode ? scheme.class : "bg-[#59d185]";

  return (
    <>
      {displayPokemon ? (
        <>
          <PickDialog
            isOpen={isPickOpen}
            closeModal={closePickModal}
            pokemon={displayPokemon}
            selectedPct={selectedPct}
            row={row}
            col={col}
          />
          <button
            onClick={openPickModal}
            //onClick={() => undoHandler(displayPokemon)}
            className={`flex h-24 w-24 cursor-pointer items-center justify-center overflow-hidden bg-cover bg-center dark:border-gray-950 ${
              selectedTab == "popular" && "border-slate-500"
            }
        } ${col < 3 && "border-r"} ${row < 3 && "border-b"}  ${
              selectedTab == "popular"
                ? "bg-slate-400"
                : storedCorrectPokemon
                ? bg
                : ""
            } transition-colors duration-75 focus-visible:z-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#59d185] dark:hover:bg-gray-700 sm:h-36 sm:w-36 md:h-48 md:w-48`}
          >
            <div className="overflow group relative flex h-full w-full items-center justify-center">
              {!isNaN(selectedPct) && (
                <div className="absolute right-0 top-0 flex h-4 items-center rounded-bl-lg bg-gray-900 pl-1.5 pr-1 text-xxs text-white opacity-95 sm:h-5 sm:pr-1.5 sm:text-xs md:h-6 md:text-sm">
                  {selectedPct < 1
                    ? selectedPct.toFixed(1)
                    : selectedPct.toFixed(0)}
                  %
                </div>
              )}
              {/* <div className="absolute left-0 top-0 flex h-4 items-center rounded-br-lg bg-gray-900 pb-0.5 pl-1 pr-1.5 pt-px text-xxs text-white opacity-95 sm:h-5  sm:pl-2 sm:pr-3 sm:text-xs md:h-6 md:text-sm">
              <svg
                className="h-3 w-3 sm:h-4 sm:w-4 md:h-5 md:w-5"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="currentColor"
                viewBox="0 0 940 1000"
              >
                <path d="M532 90c113.333 0 209.667 40 289 120s119 176.667 119 290-39.667 210-119 290-175.667 120-289 120c-92 0-176-29.333-252-88l70-76c54.667 40 115.333 60 182 60 84 0 156-30 216-90s90-132 90-216c0-85.333-30-158-90-218s-132-90-216-90c-82.667 0-153.667 28.667-213 86s-90.333 127.333-93 210h142L184 694 0 488h124c2.667-110.667 43.667-204.667 123-282S421.333 90 532 90m-36 190h70v204l130 130-50 50-150-150V280" />
              </svg>
            </div> */}

              <PokemonImage
                pokemon={displayPokemon}
                className={`absolute p-4 pt-0 ${
                  selectedTab == "no_spoilers" ? "opacity-80 contrast-0" : ""
                }`}
              />

              {selectedTab != "no_spoilers" && (
                <>
                  {/* <div className="absolute bottom-0 left-0 right-0">
                  {selectedTab == "my" && !undoUsed && (
                    <div
                      className={`line-clamp-1 ${
                        undoModeActive ? "flex" : "hidden"
                      } h-24 items-center justify-center bg-gradient-to-b from-gray-900/0 from-0% to-gray-900/95 py-px text-xxs text-white sm:h-36 sm:text-xs sm:group-hover:flex md:h-48 md:text-sm`}
                    >
                      <svg
                        className="mr-2 h-4 w-4"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="currentColor"
                        viewBox="0 0 940 1000"
                      >
                        <path d="M532 90c113.333 0 209.667 40 289 120s119 176.667 119 290-39.667 210-119 290-175.667 120-289 120c-92 0-176-29.333-252-88l70-76c54.667 40 115.333 60 182 60 84 0 156-30 216-90s90-132 90-216c0-85.333-30-158-90-218s-132-90-216-90c-82.667 0-153.667 28.667-213 86s-90.333 127.333-93 210h142L184 694 0 488h124c2.667-110.667 43.667-204.667 123-282S421.333 90 532 90m-36 190h70v204l130 130-50 50-150-150V280" />
                      </svg>{" "}
                      Undo
                    </div>
                  )}
                </div> */}
                  <div className="absolute bottom-0 left-0 right-0">
                    {/* <div className="line-clamp-1 hidden h-40 items-center justify-center bg-gradient-to-b from-gray-900/0 from-0% to-gray-900/95 py-px text-xxs text-white group-hover:flex  sm:text-xs md:text-sm">
                    <svg
                      className="mr-2 h-4 w-4 "
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                      viewBox="0 0 940 1000"
                    >
                      <path d="M532 90c113.333 0 209.667 40 289 120s119 176.667 119 290-39.667 210-119 290-175.667 120-289 120c-92 0-176-29.333-252-88l70-76c54.667 40 115.333 60 182 60 84 0 156-30 216-90s90-132 90-216c0-85.333-30-158-90-218s-132-90-216-90c-82.667 0-153.667 28.667-213 86s-90.333 127.333-93 210h142L184 694 0 488h124c2.667-110.667 43.667-204.667 123-282S421.333 90 532 90m-36 190h70v204l130 130-50 50-150-150V280" />
                    </svg>{" "}
                    Undo
                  </div> */}
                    <div className="overflow-hidden text-ellipsis whitespace-nowrap bg-gray-900/70 py-px text-xxs text-white sm:text-xs md:text-sm">
                      {displayPokemon.display_name}
                      {/* {displayPokemon.name.includes(" (")
                        ? displayPokemon.name.substring(
                            0,
                            displayPokemon.name.indexOf(" (")
                          )
                        : displayPokemon.name} */}
                    </div>
                  </div>
                </>
              )}
            </div>
          </button>
        </>
      ) : (
        <button
          onClick={openModal}
          className={`flex h-24 w-24 cursor-pointer items-center justify-center overflow-hidden dark:border-gray-950 ${
            selectedTab == "popular" && "border-slate-500"
          }
        } ${col < 3 && "border-r"} ${row < 3 && "border-b"}  ${
            selectedTab == "popular"
              ? "bg-slate-400"
              : storedCorrectPokemon
              ? bg
              : isOpen
              ? "bg-yellow-200"
              : selectedTab == "no_spoilers"
              ? ""
              : guessesLeft > 0
              ? "hover:bg-yellow-50"
              : ""
          } transition-colors duration-75 focus-visible:z-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#59d185] dark:hover:bg-gray-700 sm:h-36 sm:w-36 md:h-48 md:w-48`}
        >
          {correctAnswers.length < 0 && (
            <div className="mb-1 flex flex-col items-center text-gray-500 sm:mb-2">
              {/* <IconWarningOutline className="item h-6 w-6 sm:h-8 sm:w-8 md:h-9 md:w-9" /> */}
              <strong className="text-xs sm:text-base md:text-lg">
                {correctAnswers.length} Players
              </strong>
            </div>
          )}
        </button>
      )}

      <Dialog
        open={isOpen}
        onClose={closeModal}
        transitionDuration={300}
        componentsProps={{
          backdrop: { style: { backgroundColor: "rgba(0, 0, 0, 0.25)" } },
        }}
        aria-labelledby="scroll-dialog-title"
        aria-describedby="scroll-dialog-description"
        className="m-4 sm:m-6 md:m-20"
        sx={{
          "& .MuiDialog-container": {
            alignItems: "flex-start",
            margin: "0px",
            display: "block",
          },
        }}
        PaperProps={{
          sx: { margin: "0 auto", borderRadius: "0.75rem", maxWidth: "36rem" },
        }}
      >
        <Combobox value={selectedPerson} onChange={selectPokemon}>
          <div className="relative">
            {MagnifyingGlassIcon}
            <Combobox.Input
              autoFocus
              onChange={(event) => setQuery(event.target.value)}
              // displayValue={(person) => person.name}
              className="h-12 w-full border-0 bg-transparent pl-11 pr-9 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-200 sm:text-sm"
              placeholder={`${correctAnswers.length} possible answers...`}
              autoComplete="off"
              autoCorrect="off"
            />
          </div>
          {filteredPeople.length > 0 && (
            <Combobox.Options
              static={true}
              className={`z-50 max-h-72 scroll-py-2 overflow-y-auto border-t border-t-gray-100 py-2 text-sm text-gray-800 dark:bg-gray-700`}
            >
              {filteredPeople.map((pokemon) => (
                /* Use the `active` state to conditionally style the active option. */
                /* Use the `selected` state to conditionally style the selected option. */
                <Combobox.Option
                  key={pokemon.id}
                  value={pokemon}
                  as={Fragment}
                  disabled={
                    cellGuesses.includes(pokemon.id) ||
                    otherCorrectGuesses.includes(pokemon.id)
                  }
                >
                  {({ active, selected }) => (
                    <li
                      className={`flex cursor-default select-none items-center justify-between px-4 py-2 leading-snug ${
                        active ? "bg-gray-100" : ""
                      }`}
                    >
                      <div
                        className={`flex items-center justify-center ${
                          !storedCorrectPokemon &&
                          cellGuesses.includes(pokemon.id) &&
                          selected &&
                          "animate-shake"
                        } ${
                          storedCorrectPokemon?.id == pokemon.id
                            ? "text-green-600"
                            : cellGuesses.includes(pokemon.id)
                            ? "text-red-600 dark:text-red-400"
                            : otherCorrectGuesses.includes(pokemon.id)
                            ? "text-slate-500"
                            : "dark:text-white"
                        }`}
                      >
                        <div className="mr-3 h-9 w-9">
                          <PokemonImage pokemon={pokemon} />
                        </div>
                        <span className="text-base">
                          {pokemon.display_name}
                        </span>

                        {/* <div className="-mt-0.5 text-xs font-normal leading-snug text-gray-500 dark:text-gray-400">
                          {pokemon.first} -{" "}
                          {pokemon.last == 2023 ? "Present" : pokemon.last}
                        </div> */}
                      </div>
                      {storedCorrectPokemon?.id == pokemon.id && (
                        <div className="fade-in animate-correct px-2.5 py-1.5 text-sm font-medium text-green-600">
                          <IconCheckLg className="h-5 w-5" />
                        </div>
                      )}
                      {!selected &&
                        !cellGuesses.includes(pokemon.id) &&
                        otherCorrectGuesses.includes(pokemon.id) && (
                          <div className="px-2.5 py-1.5 text-sm font-medium text-slate-500">
                            Used
                          </div>
                        )}
                      {cellGuesses.includes(pokemon.id) && !undoUsed && (
                        <UndoButton clickHandler={() => undoHandler(pokemon)} />
                      )}
                    </li>
                  )}
                </Combobox.Option>
              ))}
            </Combobox.Options>
          )}
        </Combobox>
      </Dialog>
    </>
  );
}
